import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  Optional,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormValidationComponent,
  InputRadioGroupComponent,
  InputTextComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { AddressDetails, Country } from '@pedix-workspace/utils';
import { catchError, distinctUntilChanged, EMPTY, filter, from, of, switchMap, tap } from 'rxjs';
import {
  IconEditComponent,
  IconMapMarkerComponent,
  IconSearchComponent,
} from '@pedix-workspace/angular-ui-icons';
import {
  AbstractControl,
  ControlContainer,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import {
  MapAddressAutocompleteItem,
  MapAddressAutocompleteResponse,
  MapAddressService,
} from './map-address.service';
import { MapAutocompleteHightlightsPipe } from '../map-autocomplete-highlights.pipe';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash-es';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScrollIntoViewDirective } from '@pedix-workspace/pedixapp-shared-catalog';
import { StadiaMapsStaticComponent } from '../stadia-maps/stadia-maps-static/stadia-maps-static.component';
import { StadiaMapsLocationPopupComponent } from '../stadia-maps/stadia-maps-location-popup/stadia-maps-location-popup.component';
import { StadiaLatLng } from '../stadia-maps/stadia-maps.types';
import { NgClass } from '@angular/common';

const EMPTY_RESULTS: MapAddressAutocompleteResponse = {
  source: 'empty',
  searchTerm: '',
  items: [],
};

@Component({
  selector: 'pxw-map-address',
  imports: [
    InputTextComponent,
    InputRadioGroupComponent,
    IconMapMarkerComponent,
    ReactiveFormsModule,
    MapAutocompleteHightlightsPipe,
    AlertComponent,
    ButtonComponent,
    StadiaMapsStaticComponent,
    StadiaMapsLocationPopupComponent,
    IconSearchComponent,
    FormsModule,
    IconEditComponent,
    ScrollIntoViewDirective,
    FormValidationComponent,
    NgClass,
  ],
  styleUrl: './map-address.component.scss',
  templateUrl: 'map-address.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class MapAddressComponent {
  private mapAddressService = inject(MapAddressService);
  private toastr = inject(ToastrService);

  readonly searchTermInput = viewChild<InputTextComponent>('searchTermInput');

  // Input signals
  readonly searchCenter = input.required<StadiaLatLng>();
  readonly isFormSubmitted = input<boolean>(false);
  readonly label = input<string>();
  readonly helper = input<string>();
  readonly placeholder = input<string>();
  readonly required = input<boolean>(false);
  readonly displayStaticMapPreview = input<boolean>(false);

  readonly selectAddressText = input.required<string>();
  readonly locateOnMapText = input.required<string>();
  readonly searchText = input.required<string>();
  readonly wrongLocationText = input.required<string>();
  readonly locationNotFoundText = input.required<string>();
  readonly selectOnMapText = input.required<string>();
  readonly wrongAddressesText = input.required<string>();
  readonly readyText = input.required<string>();
  readonly geolocationErrorText = input.required<string>();
  readonly searchErrorText = input<string>('Unknown error in search');
  readonly mapInstructions = input<string[]>();

  readonly updateTimezone = input<boolean>(false);
  readonly limit = input<number>(5);
  readonly searchTermMinLength = input<number>(3);
  readonly countryCodes = input<Country[]>([]);

  // Model and outputs
  readonly addressDetails = model<AddressDetails | null>(null);
  readonly placeDetailsChange = output<void>();

  // Internal signals
  readonly searchTerm = signal<string>('');
  readonly lastSearchTerm = signal<string>('');
  readonly selectedPrediction = signal<MapAddressAutocompleteItem | null>(null);
  readonly searchResults = signal<MapAddressAutocompleteResponse>(EMPTY_RESULTS);
  readonly searchResultsCache = signal<Record<string, MapAddressAutocompleteResponse>>({});
  readonly loadingPredictions = signal(false);
  readonly loadingDetails = signal(false);
  readonly loadingGeocoding = signal(false);
  readonly mapPopupOpen = signal(false);
  readonly cachedResults = signal<Map<string, AddressDetails>>(new Map());

  // Computed signals
  readonly resultsQty = computed(() => {
    return this.searchResults()?.items.length || 0;
  });
  readonly addressNotFound = computed(() => {
    return (
      this.searchResults()?.searchTerm.length >= this.searchTermMinLength() &&
      this.resultsQty() === 0
    );
  });
  readonly loading = computed(() => {
    return this.loadingPredictions() || this.loadingDetails() || this.loadingGeocoding();
  });
  readonly requiresAddressConfirmation = computed(() => {
    const details = this.addressDetails();
    if (!details || details.useCustomLocation || details.streetNumber) {
      return false;
    }
    switch (details.resultType) {
      case 'houseNumber':
      case 'intersection':
      case 'place':
        return false;
      default:
        return true;
    }
  });

  addressDetailsValidation = (control: AbstractControl): ValidationErrors | null => {
    if (this.required() && !this.addressDetails() && this.resultsQty() === 0) {
      return { required: true };
    }
    return null;
  };

  constructor() {
    toObservable(this.selectedPrediction)
      .pipe(
        distinctUntilChanged((previousPrediction, currentPrediction) => {
          return previousPrediction === currentPrediction;
        }),
        filter((selectedPrediction): selectedPrediction is MapAddressAutocompleteItem =>
          Boolean(selectedPrediction),
        ),
        tap(() => {
          this.loadingDetails.set(true);
        }),
        switchMap(selectedPrediction => {
          const cached = this.cachedResults().get(selectedPrediction.id);
          if (cached) {
            return of(cached);
          }

          return from(
            this.mapAddressService
              .placeDetails({
                source: this.searchResults().source,
                selectedPrediction,
                updateTimezone: this.updateTimezone() || false,
                searchTerm: this.searchTerm(),
              })
              .then(addressDetails => {
                this.cachedResults.update(cache => {
                  cache.set(selectedPrediction.id, addressDetails);
                  return cache;
                });
                return addressDetails;
              }),
          ).pipe(
            catchError(error => {
              console.error(error);
              this.loadingDetails.set(false);
              this.selectedPrediction.set(null);
              this.addressDetails.set(null);

              this.toastr.error(this.searchErrorText());

              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(addressDetails => {
        this.loadingDetails.set(false);
        this.addressDetails.set(addressDetails);
      });
  }

  ngOnInit(): void {
    this.searchTerm.set(this.addressDetails()?.freeSearch || '');
    this.lastSearchTerm.set(this.addressDetails()?.freeSearch || '');
  }

  onSearchTermKeyUp(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.onSearchAddress();
    } else if (event.key === 'Escape') {
      this.searchTerm.set(this.lastSearchTerm() || '');
    }
  }

  onSearchTermFocusOut() {
    if (
      this.searchResults().searchTerm !== this.searchTerm() &&
      this.searchTerm() !== this.lastSearchTerm()
    ) {
      this.onSearchAddress();
    }
  }

  async onSearchAddress() {
    const searchTerm = this.searchTerm().trim() || '';

    this.lastSearchTerm.set(searchTerm);

    if (!searchTerm || searchTerm.length < this.searchTermMinLength()) {
      this.searchResults.set(EMPTY_RESULTS);
    } else {
      this.resetSelection();

      const cache = this.searchResultsCache();

      if (cache[searchTerm]) {
        this.searchResults.set(cloneDeep(cache[searchTerm]));
      } else {
        this.loadingPredictions.set(true);

        const requestParams = {
          q: searchTerm,
          at: `${this.searchCenter().lat},${this.searchCenter().lng}`,
          limit: this.limit(),
          countryCodes: this.countryCodes(),
        };

        try {
          const searchResults = await this.mapAddressService.autocomplete(requestParams);

          this.searchResultsCache.update(cache => ({
            ...cache,
            [searchResults.searchTerm]: searchResults,
          }));

          this.searchResults.set(searchResults);
        } catch {
          this.toastr.error(this.searchErrorText());
        } finally {
          this.loadingPredictions.set(false);
        }
      }
    }
    if (this.searchResults().items.length === 1) {
      this.selectedPrediction.set(this.searchResults().items[0]);
    }
  }

  resetSelection(resetSearchTerm = false) {
    this.searchResults.set(EMPTY_RESULTS);
    this.selectedPrediction.set(null);

    if (resetSearchTerm) {
      this.searchTerm.set('');
    } else {
      setTimeout(() => {
        this.searchTermInput()?.focus();
      }, 0);
    }

    this.addressDetails.set(null);
  }

  openMap() {
    this.mapPopupOpen.set(true);
  }

  onMarkerChanged() {
    this.addressDetails.set(null);
  }

  protected async onMarkerLocatedOnMap(marker: StadiaLatLng | null) {
    if (!marker) {
      return;
    }

    this.loadingGeocoding.set(true);

    try {
      const addressDetails = await this.mapAddressService.reverseGeocoding({
        latitude: marker.lat,
        longitude: marker.lng,
        updateTimezone: this.updateTimezone() || false,
        searchTerm: this.searchTerm(),
      });

      this.addressDetails.set(addressDetails);
    } catch {
      this.toastr.error(this.searchErrorText());
      this.addressDetails.set(null);
    } finally {
      this.loadingGeocoding.set(false);
    }
  }
}
